import { parseClientConfig, type ClientConfig } from '@ferro/config';

// Switchback Pet Lodge — FICTIONAL Denver-foothills daycare + boarding +
// grooming business. This is the platform's standing DEMO TENANT for sales
// demos and onboarding walkthroughs (C0 packet / #458 pragmatic path): a
// believable, warm, established brand that is deliberately NOT HTHD (HTHD
// owns blue) and NOT ALA (ALA owns copper/terracotta). Nothing here belongs
// to a real business; the address, phone, and email are fictional.
//
// Brand: WPA national-park-poster energy — deep pine, granite charcoal,
// aspen gold on warm cream; Zilla Slab headings, Source Sans 3 body.
// Logo suite is hand-built SVG in clients/switchback/assets/ (sources
// committed alongside the rasterized PNGs).
const config: ClientConfig = parseClientConfig({
  id: 'switchback',
  brand: {
    name: 'Switchback Pet Lodge',
    tagline: 'Play hard. Rest easy.',
    colors: {
      primary: '#1F4A38',     // Deep pine — headers, CTAs
      secondary: '#2F3437',   // Granite charcoal — chrome, accents
      accent: '#E4A93C',      // Aspen gold — highlights, rewards
      text: '#23302B',
      background: '#F6F3EC',  // Warm cream
    },
    fonts: {
      heading: 'Zilla Slab',
      body: 'Source Sans 3',
    },
    // Served by the customer-app's public dir (HTHD precedent — see
    // clients/hthd/config.ts for the rationale): absolute URLs so the
    // admin-app and email templates can consume the same art. The files
    // are committed at apps/customer-app/public/tenants/switchback/.
    logo: 'https://switchback.animalloversapp.com/tenants/switchback/logo-full-color.png',
    // Light-strokes lockup for the dark admin sidebar (AppShell prefers
    // logoDark on its --brand-secondary background). Legible at the 44px
    // sidebar height.
    logoDark: 'https://switchback.animalloversapp.com/tenants/switchback/logo-ondark.png',
    // Kate K13 — logo roles (see clients/hthd/config.ts). The horizontal
    // lockup is clients/switchback/assets/logo.png; the mark is the roundel
    // (badge.svg rasterized to logo-mark.png).
    logoHorizontal: 'https://switchback.animalloversapp.com/tenants/switchback/logo-horizontal.png',
    logoMark: 'https://switchback.animalloversapp.com/tenants/switchback/logo-mark.png',
    logoOnDark: 'https://switchback.animalloversapp.com/tenants/switchback/logo-ondark.png',
  },
  vertical: 'pet-services',
  // Same module list as clients/hthd/config.ts — the demo tenant must
  // exercise exactly the surfaces a real tenant gets ('staff-mgmt' stays
  // deferred for the same reason it is for HTHD).
  modules: [
    'agreements',
    'auth',
    'booking',
    'crm',
    'loyalty',
    'messaging',
    'notifications',
    'payments',
  ],
  business: {
    services: ['daycare', 'daycare-half', 'boarding', 'grooming'],
    pricing: {
      // These MIRROR the booking engine's global ServiceType truth
      // (apps/server/prisma/seed-hthd-catalog.ts — the ServiceType table
      // is global/shared): display config must never disagree with what
      // checkout charges (#272 quote == receipt).
      'daycare': 4700,
      'daycare-half': 3700,
      'boarding': 7000,
      'grooming': 8500, // starting rate — groom quotes finalize at the appointment
    },
    locations: [
      { name: 'Golden', address: '1420 Switchback Trail, Golden, CO 80403' },
    ],
    referralPrefix: 'SPL',
  },
  loyalty: {
    // Mirrors HTHD's ladder (100 pts = $10) so demo walkthroughs of the
    // rewards flow match the numbers staff will see on a real tenant.
    earnRate: 1,
    serviceMultipliers: { grooming: 1.5 },
    redemptionTiers: { '100': 1000, '250': 2500, '500': 5000 },
  },
  deployment: {
    // Same shared Railway platform server as every tenant; pickTenantConfig
    // overrides apiUrl at boot from the ALA config (single source of truth).
    apiUrl: 'https://animalloversapp-platform-api-production.up.railway.app',
    customerAppUrl: 'https://switchback.animalloversapp.com',
    adminAppUrl: 'https://switchback.admin.animalloversapp.com',
  },
  integrations: {
    // Demo tenant NEVER moves real money — the gateway guard's honest
    // fallback covers an unconfigured tenant, and simulation keeps every
    // demo checkout click safe by construction.
    stripe: { mode: 'simulation' },
    openai: { model: 'gpt-4o' },
    pos: null,
  },
});

export default config;
